// LearnLayout.jsx - Clean, content-focused layout for educational pages (Tier 1 & 2)
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Mail } from 'lucide-react';

export default function LearnLayout({ children, breadcrumb }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2.5 group">
              <div className="w-9 h-9 bg-[#172A3A] rounded-xl flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <span className="text-[#172A3A] font-bold text-lg group-hover:text-[#FFB800] transition-colors">
                Pro IRP
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/learn" className="text-sm text-gray-600 hover:text-[#172A3A] hidden sm:inline font-medium">
                Resources
              </Link>
              <Link to="/blog" className="text-sm text-gray-600 hover:text-[#172A3A] hidden sm:inline font-medium">
                Blog
              </Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-[#172A3A] hidden sm:inline font-medium">
                Pricing
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#FFB800] text-[#172A3A] font-bold px-4 py-2 rounded-lg hover:bg-[#e5a600] transition-all hover:scale-105 text-sm shadow-md"
              >
                Get Started
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      {breadcrumb && (
        <div className="bg-[#172A3A]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
            <nav className="flex items-center gap-2 text-sm text-blue-200/80">
              <BookOpen size={14} className="text-[#FFB800]" />
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span className="text-blue-400/50">/</span>
              <Link to="/learn" className="hover:text-white transition-colors">Resources</Link>
              <span className="text-blue-400/50">/</span>
              <span className="text-white font-medium truncate max-w-[250px] sm:max-w-none">{breadcrumb}</span>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#172A3A] text-white mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <div className="grid sm:grid-cols-3 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center">
                  <span className="text-[#172A3A] font-bold text-sm">P</span>
                </div>
                <span className="font-bold text-lg">Pro IRP</span>
              </div>
              <p className="text-sm text-blue-200/60 leading-relaxed">
                The Medicare retention platform built by agents, for agents.
                Protect your book of business with AI-powered insights.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#FFB800] text-sm uppercase tracking-wider">Resources</h4>
              <ul className="space-y-2.5 text-sm text-blue-200/80">
                <li><Link to="/learn" className="hover:text-white transition-colors">Knowledge Center</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#FFB800] text-sm uppercase tracking-wider">Get Started</h4>
              <p className="text-sm text-blue-200/60 mb-4">
                See how Pro IRP can help protect your Medicare book.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#FFB800] text-[#172A3A] font-bold px-5 py-2.5 rounded-lg hover:bg-[#e5a600] transition-all text-sm"
              >
                Start Free Trial
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="border-t border-blue-400/15 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-blue-200/40">
              &copy; {new Date().getFullYear()} Pro IRP. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-blue-200/50">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
